import React, { useEffect, useRef, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { Loader2, Sparkles, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion'
import { ChatPanel } from './ChatPanel'
import type { OutOfScopePrompt } from '@/types/DashboardV1.types'
import type { RootState } from '@/redux'
import {
  selectEditFlowContext,
  selectIsBootstrapping,
  selectEditChatError,
} from '@/redux/slice/dashboardEditChatSlice'
import { selectResolveDomainDisplayName } from '@/redux/slice/dashboardDomainRegistrySlice'
import { Button } from '@/components/ui/button'
import { ButtonVariant } from '@/constants/button/button'

// ─── Bootstrap intro message helper ──────────────────────────────────────────

/**
 * Derives a human-readable intro message from the bootstrap flow context.
 *
 * Rendered once in the overlay when the edit session is first opened.
 * Gives the user a clear summary of what the AI "sees" before they start
 * chatting. This is a client-synthesised message — not persisted to the backend.
 *
 * Format: "I can see your [Dashboard Name] dashboard — it has [N] panel(s)
 * in [Domain] with [Season] data. What would you like to change?"
 */
function buildBootstrapIntroMessage(args: {
  dashboardName: string
  panelCount: number
  selectedDomainIds: string[]
  domainFilterValues: Record<string, string | string[]>
  resolveDomainDisplayName: (id: string) => string
}): string {
  const { dashboardName, panelCount, selectedDomainIds, domainFilterValues, resolveDomainDisplayName } = args

  const domainLabel =
    selectedDomainIds.length > 0
      ? selectedDomainIds.map(resolveDomainDisplayName).join(', ')
      : null

  // Look for a season filter — common to enrollment dashboards.
  // Check both bare 'season' and any namespaced variant like '<domainId>.season'.
  const seasonValue =
    domainFilterValues['season'] ??
    Object.entries(domainFilterValues).find(([k]) => k.endsWith('.season'))?.[1]

  const panelLabel = panelCount === 1 ? '1 panel' : `${panelCount} panels`

  const parts: string[] = [
    `I can see your "${dashboardName}" dashboard`,
    `— it has ${panelLabel}`,
  ]

  if (domainLabel) {
    parts.push(`in ${domainLabel}`)
  }

  if (seasonValue != null) {
    const season = Array.isArray(seasonValue) ? seasonValue.join(', ') : seasonValue
    parts.push(`for ${season}`)
  }

  return parts.join(' ') + '. What would you like to change?'
}

interface ChatOverlayProps {
  dashboardId: string
  isOpen: boolean
  onClose: () => void
  onExpandScope?: (prompt: OutOfScopePrompt) => void
  /**
   * Ref passed down from Canvas so it can focus the toggle button when the
   * overlay closes (brand guide §36 Focus management).
   */
  toggleButtonRef?: React.RefObject<HTMLButtonElement>
}

/**
 * ChatOverlay — the v2 position:fixed right-edge slide-over overlay shell.
 *
 * Per brand guide §36 (Post-Creation Viewing Mode):
 *  - Width: 460px default, 380px below 1100px viewport.
 *  - Position: fixed, right edge, top-[60px] (below Canvas top bar), full height.
 *  - Z-index: z-30 (above canvas z-0, below top bar z-20).
 *  - Animation: 300ms cubic-bezier(0.4, 0, 0.2, 1) translateX slide-in.
 *    Reduced-motion fallback: opacity 0→1 at 150ms.
 *  - No backdrop — dashboard remains fully visible.
 *  - Inert management: when closed, all focusable descendants removed from tab order.
 *  - Focus: textarea on open, toggle button on close.
 *
 * The overlay shell owns positioning, animation, header, focus management, and
 * the Escape key handler. Chat content (messages + input) lives in <ChatPanel>.
 */
export const ChatOverlay: React.FC<ChatOverlayProps> = ({
  dashboardId,
  isOpen,
  onClose,
  onExpandScope,
  toggleButtonRef,
}) => {
  const prefersReducedMotion = usePrefersReducedMotion()
  const overlayRef = useRef<HTMLDivElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  // We rely on ChatPanel's internal textareaRef for input focus.
  // We pass a focusInput callback so the overlay can trigger it on open.
  const focusInputRef = useRef<(() => void) | null>(null)

  // ── Edit session context ────────────────────────────────────────────────
  // Read the edit session state to render the bootstrap intro message and
  // any bootstrapping/error states in the overlay chrome.
  const isBootstrapping = useSelector(selectIsBootstrapping)
  const flowContext = useSelector(selectEditFlowContext)
  const editChatError = useSelector(selectEditChatError)
  const dashboardName = useSelector((state: RootState) => state.dashboards.selectedDashboard?.name)
  // Registry-aware display-name resolver. Replaces the deleted
  // resolveDomainDisplayName helper from src/constants/domains.ts (gutted by
  // the SDK-Owned Domain Metadata Refactor bundle, 2026-05-09).
  const resolveDomainDisplayName = useSelector(selectResolveDomainDisplayName)

  // Derive the bootstrap intro message from flow context fields (AC #1).
  // Rendered as a system-authored "here's what I see" message once per session.
  const bootstrapIntroMessage = flowContext
    ? buildBootstrapIntroMessage({
        dashboardName: dashboardName ?? 'your dashboard',
        panelCount: flowContext.panelSnapshot.length,
        selectedDomainIds: flowContext.selectedDomainIds,
        domainFilterValues: flowContext.domainFilterValues,
        resolveDomainDisplayName,
      })
    : null

  // Inert management: remove overlay from tab order when closed (WCAG 2.4.3)
  const setInert = useCallback((node: HTMLDivElement | null) => {
    if (!node) return
    if (!isOpen) {
      node.setAttribute('inert', '')
    } else {
      node.removeAttribute('inert')
    }
  }, [isOpen])

  // Combined ref: assign both overlayRef and inert-setter
  const combineRefs = useCallback((node: HTMLDivElement | null) => {
    (overlayRef as React.MutableRefObject<HTMLDivElement | null>).current = node
    setInert(node)
  }, [setInert])

  // Focus management on open/close (brand guide §36 Focus management)
  useEffect(() => {
    if (isOpen) {
      // Focus the chat input when overlay opens
      // Use rAF to ensure the inert attribute is removed first
      requestAnimationFrame(() => {
        focusInputRef.current?.()
      })
    } else {
      // Return focus to the toggle button when overlay closes
      toggleButtonRef?.current?.focus()
    }
  }, [isOpen, toggleButtonRef])

  // Escape key handler — closes overlay and returns focus to toggle (brand guide §36)
  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  return (
    <div
      ref={combineRefs}
      role="complementary"
      aria-label="AI Assistant"
      aria-hidden={!isOpen}
      data-state={isOpen ? 'open' : 'closed'}
      className={cn(
        // Position: fixed right edge, below top bar (60px), full height to viewport bottom.
        // Canvas top bar: py-3 (12px × 2) + h-9 (36px) tallest child = 60px.
        'fixed right-0 top-[60px] bottom-0 z-30',
        // Width: 460px default, 380px below 1100px (brand guide §36 Viewport breakpoints v2)
        'w-[460px] max-[1100px]:w-[380px]',
        // Chrome: card bg, left border, shadow
        'flex flex-col bg-card border-l border-border',
        'shadow-[-8px_0_32px_rgba(15,23,42,0.10)]',
        // Animation (brand guide §36 Animation)
        prefersReducedMotion
          ? cn(
              // duration-150 (no motion-safe prefix) — we're already inside the
              // reduced-motion branch, so motion-safe would be a no-op here.
              'transition-opacity duration-150',
              isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
            )
          : cn(
              'motion-safe:transition-[transform] motion-safe:duration-300 motion-safe:ease-in-out',
              isOpen ? 'translate-x-0' : 'translate-x-full',
            ),
      )}
    >
      {/* ── Overlay header (brand guide §36 Overlay header) ──────── */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border flex-shrink-0 bg-card">
        {/* AI agent avatar — 30px (w-8 h-8) per brand guide §36 */}
        <div
          className="w-8 h-8 rounded-full bg-gradient-to-br from-accent to-blue-700 flex items-center justify-center flex-shrink-0"
          aria-hidden="true"
        >
          <Sparkles size={14} className="text-white" aria-hidden="true" />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-primary leading-tight">
            TheCampStack AI Agent
          </p>
          <p className="text-xs text-secondary">Ask questions about your dashboard</p>
        </div>

        {/* Close Chat Window */}
        <Button
          className='w-8 h-8 p-0 rounded-xl border-2 border-border flex items-center justify-center hover:border-secondary text-secondary hover:text-primary'
          variant={ButtonVariant.ICON}
          onClick={onClose}
          ref={closeButtonRef}
          type='button'
        >
          <X size={14} aria-hidden="true" />
        </Button>
      </div>

      {/* ── Chat body — messages + input ─────────────────────────── */}
      {/* ChatPanel variant="viewingDrawer" handles all message rendering and input.
          We render it unconditionally so Redux state (messages, session) is preserved
          when overlay is open/closed without remounting.
          Bootstrap intro message is rendered above ChatPanel's messages area. */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">

        {/* Bootstrap loading state — shown while the edit session is being opened */}
        {isBootstrapping && (
          <div className="flex items-center gap-2 px-5 py-3 border-b border-border bg-muted/40 flex-shrink-0">
            <Loader2 size={13} className="animate-spin text-secondary flex-shrink-0" aria-hidden="true" />
            <span className="text-xs text-secondary">Opening edit session…</span>
          </div>
        )}

        {/* Bootstrap error state — shown when the session failed to open */}
        {editChatError && !isBootstrapping && (
          <div
            role="alert"
            className="flex items-start gap-2 px-5 py-3 border-b border-destructive bg-destructive/5 flex-shrink-0"
          >
            <span className="text-xs text-destructive leading-relaxed">{editChatError}</span>
          </div>
        )}

        {/* Bootstrap intro message — system-authored summary rendered once per session.
            Rendered as an AI avatar + message bubble, matching the ChatPanel message style.
            Not persisted; disappears on session clear / navigation. */}
        {bootstrapIntroMessage && !isBootstrapping && (
          <div className="flex gap-3 items-start px-5 pt-5 pb-1 flex-shrink-0 bg-background">
            <div
              className="w-8 h-8 rounded-full bg-accent flex items-center justify-center flex-shrink-0"
              aria-hidden="true"
            >
              <Sparkles size={14} className="text-foreground" aria-hidden="true" />
            </div>
            <div className="flex-1 min-w-0">
              <div
                className="bg-foreground border border-border rounded-2xl rounded-tl-sm px-5 py-4 text-sm text-primary max-w-full shadow-[0_1px_4px_rgba(15,23,42,0.06)]"
                data-testid="bootstrap-intro-message"
              >
                {bootstrapIntroMessage}
              </div>
            </div>
          </div>
        )}

        <ChatPanel
          dashboardId={dashboardId}
          variant="viewingDrawer"
          isOpen={isOpen}
          onCloseDrawer={onClose}
          onExpandScope={onExpandScope}
          onFocusInputReady={(fn) => { focusInputRef.current = fn }}
          hideInternalHeader={true}
        />
      </div>
    </div>
  )
}
